import { useCallback, useEffect, useMemo, useState } from 'react';
import useGlobals from './useGlobals';
import { useParams } from 'react-router-dom';
import { Credential, EmbeddingModel } from '../Types';
import { DLPState } from '../Types/Enums';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { RootState } from '../Store/Reducers';
import { embeddingModelService } from '../Services/embeddingModelService';
import { credentialsService } from '../Services/credentialsService';
import { setReloadSettings } from '../Store/Actions/settingsActions';

const useEmbeddingModel = () => {
    const { handleNavigate, userSettings, i18n, dispatch, settings } = useGlobals();

    let { embeddingModelIdentifier } = useParams();
    const [embeddingModelSnapshot, setEmbeddingModelSnapshot] = useState<EmbeddingModel>();
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const icapAddress = useSelector((state: RootState) => state.settings.icapAddress);
    const [credentials, setCredentials] = useState<Credential[]>([]);
    const [loadingState, setLoadingState] = useState({
        modelLoaded: false,
        llmModelLoaded: false,
        credentialsLoaded: false,
    });
    const [embeddingModel, setEmbeddingModel] = useState<EmbeddingModel>({
        identifier: '',
        title: '',
        value: '',
        costs: 0.0,
        settings: {},
        image: '',
        dlp: DLPState.dlp_optional,
        icapDlpActive: false,
        credential: null,
    });

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'toast', sourceName: 'Model' }));
    }, [i18n]);

    const handleChangeModel = useCallback((key: keyof EmbeddingModel, value: any) => {
        setEmbeddingModel((prevModel) => ({
            ...prevModel,
            [key]: value,
        }));
    }, []);

    const updateEmbeddingModel = useCallback((updates: Partial<EmbeddingModel>) => {
        setEmbeddingModel((prevModel) => ({
            ...prevModel,
            ...updates,
        }));
    }, []);

    const handleImageChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                setImagePreview(URL.createObjectURL(file));
                handleChangeModel('image', file);
            }
        },
        [handleChangeModel],
    );

    const handleCredentialDropdownChange = useCallback(
        (value: string) => {
            const selectedCredential = credentials.find((credential) => credential.identifier === value);
            if (selectedCredential) {
                handleChangeModel('credential', selectedCredential);
            }
        },
        [credentials, handleChangeModel],
    );

    const handleRemoveImage = useCallback(() => {
        setImagePreview(null);
        handleChangeModel('image', null);
    }, [handleChangeModel]);

    const handleSave = useCallback(() => {
        if (!userSettings.isLoggedIn) return;
        const data = new FormData();
        data.append('title', embeddingModel.title);
        data.append('value', embeddingModel.value);
        data.append('costs', (embeddingModel.costs / 1000000).toString());
        data.append('dlp', embeddingModel.dlp);
        data.append('icapDlpActive', embeddingModel.icapDlpActive ? 'true' : 'false');
        if (embeddingModel.credential) {
            data.append('credential', embeddingModel.credential.identifier);
        }

        data.append('settings', JSON.stringify(embeddingModel.settings));

        if (embeddingModel.image instanceof File || embeddingModel.image instanceof Blob) {
            const fileType = embeddingModel.image.type.split('/')[1];
            const fileName = `upload.${fileType}`;
            data.append('image', new File([embeddingModel.image], fileName, { type: embeddingModel.image.type }));
        } else {
            // TODO: Micha fragen ob es gewollt ist, dass ein Bild nicht entfernt werden kann
            // data.append('image', '');
        }

        const type = embeddingModelIdentifier === 'new' ? 'create' : 'update/' + embeddingModelIdentifier;

        embeddingModelService.saveModel(type, data).then((response) => {
            embeddingModelIdentifier === 'new' && handleNavigate('/admin/embeddingmodels/' + response.data.identifier);
            setEmbeddingModelSnapshot(response.data);
            notifySuccess();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            if (!settings.embeddingModel) {
                dispatch(setReloadSettings(true));
            }
        });
    }, [
        embeddingModel,
        embeddingModelIdentifier,
        userSettings.isLoggedIn,
        handleNavigate,
        notifySuccess,
        dispatch,
        settings.embeddingModel,
    ]);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;

        credentialsService.getCredentials().then((response) => {
            setCredentials(response.data.data);
            setLoadingState((prevState) => ({ ...prevState, credentialsLoaded: true }));

            if (embeddingModelIdentifier !== 'new' && embeddingModelIdentifier !== undefined) {
                embeddingModelService.getModel(embeddingModelIdentifier).then((response) => {
                    const modelData = {
                        ...response.data,
                        costs: response.data.costs ? response.data.costs * 1000000 : 0,
                    };
                    setEmbeddingModel(modelData);
                    setImagePreview(response.data.image);
                    setLoadingState((prevState) => ({ ...prevState, modelLoaded: true }));
                    setEmbeddingModelSnapshot(response.data);
                });
            } else {
                setLoadingState((prevState) => ({ ...prevState, modelLoaded: true }));
            }
        });
    }, [userSettings.isLoggedIn, embeddingModelIdentifier]);

    const EmbeddingModelActions = useMemo(
        () => ({
            handleImageChange,
            handleSave,
            handleChangeModel,
            updateEmbeddingModel,
            handleRemoveImage,
            handleCredentialDropdownChange,
        }),
        [
            handleImageChange,
            handleSave,
            handleChangeModel,
            updateEmbeddingModel,
            handleRemoveImage,
            handleCredentialDropdownChange,
        ],
    );

    return {
        EmbeddingModelActions,
        handleNavigate,
        embeddingModelSnapshot,
        embeddingModel,
        imagePreview,
        icapAddress,
        i18n,
        loadingState,
        embeddingModelIdentifier,
        credentials,
    };
};

export default useEmbeddingModel;
